import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../libraries/auth'
import { useI18N } from '../../../libraries/i18n/hook'
import TextInput from '../../components/form/TextInput'
import Button from '../../components/form/Button'
import FormGroup from '../../components/form/FormGroup'
import ErrorMessage from '../../components/errors/ErrorMessage'
import { ANALYTICS_EVENTS, useAnalytics } from '../../../libraries/posthog'
const AccountVerificationForm = () => {
  const [searchParams] = useSearchParams()
  const [username, setUsername] = useState(searchParams.get('username') || '')
  const navigate = useNavigate()
  const { verifyEmail, resendVerificationCode } = useAuth()
  const { t } = useI18N()
  const { trackEvent } = useAnalytics()

  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState(false)
  const [validationSuccess, setValidationSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      const result = await verifyEmail(username, code)
      if (!result.success) {
        setError(result.error || 'Failed to verify email')
        return
      }
      setValidationSuccess(true)
      trackEvent(ANALYTICS_EVENTS.SIGNUP_EMAIL_VERIFIED)
      setTimeout(() => {
        navigate(`/login?username=${encodeURIComponent(username)}`)
      }, 3000)
    } catch (err) {
      setError(`An unexpected error occurred ${err}`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    try {
      const result = await resendVerificationCode(username)
      if (!result.success) {
        setError(result.error || 'Failed to resend code')
        return
      }
      setResendSuccess(true)
      setError('')
      setTimeout(() => setResendSuccess(false), 5000)
    } catch (err) {
      setError(`Failed to resend code: ${err}`)
      setResendSuccess(false)
    }
  }

  const showMessage = (message: string) => (<div className='p-4 mb-4 text-sm text-green-700 bg-green-100 rounded-lg'>{message}</div>)

  // Determine which message to show based on priority
  const getMessage = () => {
    if (validationSuccess) {
      return t('auth.verify.codeVerified')
    } else if (resendSuccess) {
      return t('auth.verify.codeSent')
    } else if (searchParams.get('username')) {
      return t('auth.verify.codeSent.auto')
    }
    return null
  }

  const messageToShow = getMessage()

  return (
    <form onSubmit={handleSubmit} className='w-full space-y-6'>
      {error && <ErrorMessage error={new Error(error)} />}
      {messageToShow && showMessage(messageToShow)}

      {!searchParams.get('username') && (
        <FormGroup>
          <TextInput
            value={username}
            label={t('auth.verify.username')}
            placeholder={t('auth.verify.username')}
            name='username'
            onChange={(e) => setUsername(e.target.value)}
            required
            className='w-full border border-gray-600'
          />
        </FormGroup>
      )}

      <FormGroup>
        <TextInput
          value={code}
          label={t('auth.verify.code')}
          placeholder={t('auth.verify.enterCode')}
          name='code'
          onChange={(e) => setCode(e.target.value)}
          required
          className='w-full border border-gray-600'
        />
      </FormGroup>

      <Button
        type='submit'
        variant='primary'
        size='md'
        loading={isLoading}
        disabled={isLoading}
        className='w-full'
      >
        {t('auth.verify.verify')}
      </Button>

      <div className='text-center'>
        <button
          type='button'
          onClick={handleResendCode}
          className='text-sm text-deep-green hover:text-gray-400'
        >
          {t('auth.verify.resendCode')}
        </button>
      </div>
    </form>
  )
}

export default AccountVerificationForm
